import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Loader2, Tv, X } from 'lucide-react';
import Navigation from '../components/Navigation';
import ChannelCard from '../components/ChannelCard';
import VideoPlayer from '../components/VideoPlayer';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrowsePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const fetchChannels = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 24
      });
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory && selectedCategory !== 'All') params.append('category', selectedCategory);

      const response = await axios.get(`${API_URL}/api/channels?${params}`, {
        withCredentials: true
      });
      setChannels(response.data.channels);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/channels/categories`, {
        withCredentials: true
      });
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/favorites`, {
        withCredentials: true
      });
      setFavorites(new Set(response.data.favorites.map(f => f.id)));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchFavorites();
  }, []);

  useEffect(() => {
    setLoading(true);
    const timeoutId = setTimeout(() => {
      fetchChannels();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [fetchChannels]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleToggleFavorite = async (channel) => {
    try {
      if (favorites.has(channel.id)) {
        await axios.delete(`${API_URL}/api/favorites/${channel.id}`, {
          withCredentials: true
        });
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(channel.id);
          return newSet;
        });
      } else {
        await axios.post(`${API_URL}/api/favorites`, { channel_id: channel.id }, {
          withCredentials: true
        });
        setFavorites(prev => new Set([...prev, channel.id]));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleChannelClick = (channel) => {
    setSelectedChannel(channel);
    // Add to watch history
    axios.post(`${API_URL}/api/history`, {
      channel_id: channel.id,
      watched_at: new Date().toISOString(),
      duration: 0
    }, { withCredentials: true }).catch(console.error);
  };

  const handleClosePlayer = () => {
    setSelectedChannel(null);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      
      <main className="pt-20 pb-12">
        {/* Video Player Modal */}
        {selectedChannel && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="player-modal">
            <div className="w-full max-w-6xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold font-['Outfit']">{selectedChannel.name}</h2>
                <button
                  onClick={handleClosePlayer}
                  className="p-2 text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A] rounded-lg transition-colors"
                  data-testid="close-player-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <VideoPlayer 
                url={selectedChannel.url} 
                channelName={selectedChannel.name}
                onTimeUpdate={(duration) => {
                  // Update watch history duration
                  axios.post(`${API_URL}/api/history`, {
                    channel_id: selectedChannel.id,
                    watched_at: new Date().toISOString(),
                    duration
                  }, { withCredentials: true }).catch(console.error);
                }}
              />
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A3A3A3]" strokeWidth={1.5} />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search channels..."
                className="input-field w-full pl-12 pr-4 py-3 text-white"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="mb-8 overflow-x-auto pb-2">
            <div className="flex gap-2 min-w-max">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`category-pill ${selectedCategory === category ? 'active' : ''}`}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Channels Grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-video bg-[#1A1A1A] rounded-lg skeleton" />
                  <div className="mt-3 h-4 bg-[#1A1A1A] rounded skeleton w-3/4" />
                </div>
              ))}
            </div>
          ) : channels.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Tv className="w-16 h-16 text-[#262626] mb-4" strokeWidth={1} />
              <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No Channels Found</h3>
              <p className="text-[#A3A3A3] max-w-md">
                {searchQuery 
                  ? `No channels match "${searchQuery}". Try a different search term.`
                  : 'No channels available. Add playlists from the admin panel.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="channels-grid">
                {channels.map((channel) => (
                  <ChannelCard
                    key={channel.id}
                    channel={channel}
                    onClick={handleChannelClick}
                    onFavorite={handleToggleFavorite}
                    isFavorite={favorites.has(channel.id)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="btn-secondary px-4 py-2 disabled:opacity-50"
                    data-testid="prev-page-btn"
                  >
                    Previous
                  </button>
                  <span className="text-[#A3A3A3] px-4">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="btn-secondary px-4 py-2 disabled:opacity-50"
                    data-testid="next-page-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default BrowsePage;
